import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { baseUrl } from "../../../utils/api/apis";
import { useSelector } from "react-redux";
import ReactTable from "../../../reusableComponents/ReactTable/ReactTable";
import Select from "react-select";
import { DateRangePicker } from "rsuite";
import "./Payoutleaderboard.scss";

const PayoutLeaderBoard = () => {
  const { idToken } = useSelector((state) => state.auth);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [highestParams, setHighestParams] = useState({
    page: 1,
    page_size: 10,
  });
  const [combinedParams, setCombinedParams] = useState({
    page: 1,
    page_size: 10,
  });
  const [filters, setFilters] = useState({
    email: "",
    account_size: "",
    country: "",
    date_from: "",
    date_to: "",
  });
  const [selectedDates, setSelectedDates] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const filtersRef = useRef(filters);

  const accountSizeOptions = useMemo(
    () => [
      { label: "All", value: "" },
      { label: "$2,500", value: 2500 },
      { label: "$5,000", value: 5000 },
      { label: "$10,000", value: 10000 },
      { label: "$25,000", value: 25000 },
      { label: "$50,000", value: 50000 },
      { label: "$100,000", value: 100000 },
    ],
    []
  );

  const fetchLeaderboard = useCallback(
    async (nextFilters) => {
      setLoading(true);
      setError(null);
      try {
        const f = nextFilters || {};
        const params = {};
        if (f.email) params.email = f.email;
        if (f.account_size) params.account_size = f.account_size;
        if (f.country) params.country = f.country;
        if (f.date_from) params.date_from = f.date_from;
        if (f.date_to) params.date_to = f.date_to;

        const response = await axios.get(
          `${baseUrl}account/admin/payout-leaderboard/`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
            params,
          }
        );
        setLeaderboardData(response?.data || null);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load leaderboard"
        );
      } finally {
        setLoading(false);
      }
    },
    [idToken]
  );

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial load: fetch once on mount with empty params
  useEffect(() => {
    if (idToken) {
      fetchLeaderboard({});
    }
  }, [idToken, fetchLeaderboard]);

  // Update filters immediately on input (no debounce) without auto-fetch
  useEffect(() => {
    setFilters((prev) => ({ ...prev, email: emailInput }));
    setHighestParams((p) => ({ ...p, page: 1 }));
    setCombinedParams((p) => ({ ...p, page: 1 }));
  }, [emailInput]);

  const combinedData = useMemo(
    () => leaderboardData?.total_combined_payout_leaderboard || [],
    [leaderboardData]
  );
  const highestData = useMemo(
    () => leaderboardData?.highest_single_payout_leaderboard || [],
    [leaderboardData]
  );

  const highestColumns = useMemo(
    () => [
      { Header: "Rank", accessor: "rank" },
      { Header: "Name", accessor: "name" },
      { Header: "Account Size", accessor: "account_size" },
      { Header: "Highest Payout", accessor: "highest_payout" },
      { Header: "Gain %", accessor: "gain_percentage" },
      { Header: "Country", accessor: "country" },
    ],
    []
  );

  const combinedColumns = useMemo(
    () => [
      { Header: "Rank", accessor: "rank" },
      { Header: "Name", accessor: "name" },
      { Header: "Total Payout", accessor: "total_payout" },
      { Header: "Country", accessor: "country" },
    ],
    []
  );

  const highestDisplayData = useMemo(() => {
    const start = (highestParams.page - 1) * highestParams.page_size;
    const end = start + highestParams.page_size;
    return highestData.slice(start, end);
  }, [highestData, highestParams]);

  const combinedDisplayData = useMemo(() => {
    const start = (combinedParams.page - 1) * combinedParams.page_size;
    const end = start + combinedParams.page_size;
    return combinedData.slice(start, end);
  }, [combinedData, combinedParams]);

  const countryOptions = useMemo(() => {
    const set = new Set();
    highestData.forEach((r) => r?.country && set.add(r.country));
    combinedData.forEach((r) => r?.country && set.add(r.country));
    const arr = Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return [
      { label: "All", value: "" },
      ...arr.map((c) => ({ label: c, value: c })),
    ];
  }, [highestData, combinedData]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="alltable filters">
        <div className="filter-div">
          <label style={{ display: "block", marginBottom: 4 }}>Email</label>
          <input
            type="text"
            value={emailInput}
            placeholder="Search by email"
            className="filter-input"
            onChange={(e) => setEmailInput(e.target.value)}
          />
        </div>
        <div className="filter-div">
          <label style={{ display: "block", marginBottom: 4 }}>
            Account size
          </label>
          <Select
            isSearchable={false}
            options={accountSizeOptions}
            value={accountSizeOptions.find(
              (opt) => opt.value === filters.account_size
            )}
            onChange={(opt) =>
              setFilters((p) => ({ ...p, account_size: opt?.value || "" }))
            }
          />
        </div>
        <div className="filter-div">
          <label style={{ display: "block", marginBottom: 4 }}>Country</label>
          <Select
            placeholder="Search by country..."
            classNamePrefix="react-select"
            options={countryOptions}
            value={
              countryOptions.find((o) => o.value === filters.country) ||
              countryOptions[0]
            }
            onChange={(option) => {
              setFilters((p) => ({ ...p, country: option?.value || "" }));
              setHighestParams((p) => ({ ...p, page: 1 }));
              setCombinedParams((p) => ({ ...p, page: 1 }));
            }}
            isClearable
          />
        </div>
        <div className="filter-div" style={{ gridColumn: "span 2" }}>
          <label style={{ display: "block", marginBottom: 4 }}>Date</label>
          <DateRangePicker
            className="datepicker-date"
            character=" – "
            placeholder="DD/MM/YYYY – DD/MM/YYYY"
            style={{ width: "100%" }}
            value={selectedDates}
            onChange={(range) => {
              setSelectedDates(range);
              if (Array.isArray(range) && range[0] && range[1]) {
                const [start, end] = range;
                const toISODate = (d) =>
                  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
                    .toISOString()
                    .slice(0, 10);
                setFilters((p) => ({
                  ...p,
                  date_from: toISODate(start),
                  date_to: toISODate(end),
                }));
              } else {
                setFilters((p) => ({ ...p, date_from: "", date_to: "" }));
              }
            }}
          />
        </div>
        <div className="filter-div"
          style={{ display: "flex", alignItems: "end", gap: 8, height: "100%" }}
        >
          <button
            className="primary"
            onClick={() => {
              setHighestParams((p) => ({ ...p, page: 1 }));
              setCombinedParams((p) => ({ ...p, page: 1 }));
              fetchLeaderboard(filters);
            }}
          >
            Apply
          </button>
          <button
            onClick={() => {
              setFilters({
                email: "",
                account_size: "",
                country: "",
                date_from: "",
                date_to: "",
              });
              setSelectedDates(null);
              setHighestParams((p) => ({ ...p, page: 1 }));
              setCombinedParams((p) => ({ ...p, page: 1 }));
              fetchLeaderboard({
                email: "",
                account_size: "",
                country: "",
                date_from: "",
                date_to: "",
              });
            }}
          >
            Reset
          </button>
        </div>
      </div>
      <div className="table_scroll_wrapper">
        <div className="alltable">
          {loading && <div>Loading payout leaderboard...</div>}
          {error && <div style={{ color: "red" }}>{error}</div>}
          <h3>Highest single payout leaderboard</h3>

          <ReactTable
            columns={highestColumns}
            data={highestDisplayData}
            params={highestParams}
            setParams={setHighestParams}
            serverSide={true}
            totalCount={highestData.length}
          />
        </div>
      </div>
      <div className="table_scroll_wrapper">
        <div className="alltable">
          <h3>Total combined payout leaderboard</h3>

          <ReactTable
            columns={combinedColumns}
            data={combinedDisplayData}
            params={combinedParams}
            setParams={setCombinedParams}
            serverSide={true}
            totalCount={combinedData.length}
          />
        </div>
      </div>
    </div>
  );
};

export default PayoutLeaderBoard;
